var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import ReactSelect, { createFilter } from 'react-select';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Magnetic as Mi } from "../../styles";
import { ClearableSelectStyles, customStyles, MessageFilterStyles } from './SelectStyles';
const MAX_MENU_HEIGHT = 200;
export const SelectStyleType = {
    customStyles: customStyles,
    ClearableSelectStyles: ClearableSelectStyles,
    MessageFilterStyles: MessageFilterStyles
};
/**
 * @param options Array of options that populate the select menu
 * @param value The value of the select
 * @param onChange Handle change events on the select
 * @param onBlur Handle blur events on the control
 * @param onMenuOpen Handle the menu opening
 * @param onMenuClose Handle the menu closing
 * @param openMenuOnFocus Open select menu when the Select is focused
 * @param isMenuOpen Open select menue
 * @param isDisabled Disable the select
 * @param isSearchable Enable search functionality
 * @param isLoading Display select in loading state
 * @param loadingMessage Text to display when select is in loading state
 * @param placeholder Placeholder for the select value
 * @param noOptionsMessage Text to display when there are no options
 * @param dropdownPxWidth Select width in px
 * @param filterOption Custom method to filter whether an option should be displayed in the menu
 * @param isMulti Enable multiple options selection
 * @param ref Allow to access select props and methods
 * @param id Unique string
 * @param styles Style modifier methods
 * @param defaultValue The initial value of the select
 * @param required Displat required indicator
 * @param customOptionComponent optinal prop to show custom options for select
 * @param customValueComponent optional prop to show custom value for select
 * @param classNamePrefix optional prop to have custom className prefix
 * @param maxMenuHeight optional prop to set custom max height of select dropdown menu.
 * @param menuPortalTarget optional prop to portal the select menu to a dom node
 * @param closeMenuOnScroll optional prop to close the select menu when the user scrolls the document/body
 * @param isValueInvalid optional prop to if any invalid option selected
 * @param hasSelectAllOptionEnabled optional prop to display an all select option for multiSelect
 * @param selectAllOptionLabel optional prop to pass the label for all select option for multiSelect
 * @param iconClass optional prop to show icon for select control
 * @param customFilterOption method to prioritize over filterOption to filter whether an option should be displayed in the dropdown mwnu
 * @param menuPlacement optional prop to set menu placement
 */
export const Select = React.forwardRef((props, ref) => {
    Select.defaultProps = {
        isDisabled: false,
        isSearchable: false,
        isLoading: false,
        isMulti: false,
        isClearable: false,
        maxMenuHeight: MAX_MENU_HEIGHT
    };
    const [selectOptions, setSelectOptions] = React.useState(props.options);
    const [selectedValue, setSelectedValue] = React.useState(props.value || null);
    const allSelectOption = {
        label: props.selectAllOptionLabel,
        value: props.selectAllOptionLabel
    };
    React.useEffect(() => {
        props.isMulti &&
            props.hasSelectAllOptionEnabled &&
            props.selectAllOptionLabel &&
            setSelectOptions([Object.assign({}, allSelectOption), ...selectOptions]);
    }, []);
    React.useEffect(() => {
        if (props.isMulti && props.selectAllOptionLabel) {
            const value = props.value;
            if (value && value.length && value[value.length - 1].label === props.selectAllOptionLabel) {
                setSelectOptions([]);
                setSelectedValue([...value.slice(-1)]);
            }
            else {
                setSelectOptions([Object.assign({}, allSelectOption), ...props.options]);
                setSelectedValue(props.value || null);
            }
        }
        else {
            setSelectedValue(props.value || null);
            setSelectOptions(props.options);
        }
    }, [props.value, props.options]);
    const CustomClearIndicator = (_a) => {
        var { innerProps } = _a, restProps = __rest(_a, ["innerProps"]);
        const clearStyles = restProps.getStyles('clearIndicator', restProps);
        return (React.createElement("div", Object.assign({}, innerProps, { style: Object.assign({}, clearStyles) }),
            React.createElement("span", { className: "mi-icon-close" })));
    };
    const CustomDropdown = (_a) => {
        var { innerProps } = _a, restProps = __rest(_a, ["innerProps"]);
        const dropdownStyles = restProps.getStyles('dropdownIndicator', restProps);
        return (React.createElement("div", Object.assign({}, innerProps, { style: Object.assign({}, dropdownStyles) }),
            React.createElement("span", { className: props.iconClass || 'mi-icon-twisty-expanded2' })));
    };
    const CustomLoading = () => (React.createElement(CircularProgress, { style: { color: Mi.colorBrand06 }, size: 20, thickness: 5 }));
    let SelectComponents = {
        ClearIndicator: CustomClearIndicator,
        DropdownIndicator: CustomDropdown,
        LoadingIndicator: CustomLoading
    };
    let customSelectComponents = {};
    if (props.customOptionComponent) {
        customSelectComponents = Object.assign(Object.assign({}, customSelectComponents), { Option: props.customOptionComponent });
    }
    if (props.customValueComponent) {
        customSelectComponents = Object.assign(Object.assign({}, customSelectComponents), { SingleValue: props.customValueComponent });
    }
    SelectComponents = Object.assign(Object.assign({}, SelectComponents), customSelectComponents);
    return (React.createElement(ReactSelect, { value: selectedValue || props.value, defaultValue: props.defaultValue, options: selectOptions, onChange: props.onChange, onBlur: props.onBlur, onMenuOpen: props.onMenuOpen, onMenuClose: props.onMenuClose, openMenuOnFocus: props.openMenuOnFocus, menuIsOpen: props.isMenuOpen, isMulti: props.isMulti, isDisabled: props.isDisabled, isSearchable: props.isSearchable, isLoading: props.isLoading, blurInputOnSelect: !props.isMulti, closeMenuOnSelect: !props.isMulti, loadingMessage: () => props.loadingMessage, placeholder: props.placeholder, noOptionsMessage: () => props.noOptionsMessage, instanceId: props.id, id: props.id, styles: props.styles ? props.styles : customStyles, components: SelectComponents, autoFocus: false, isClearable: props.isClearable, menuPlacement: props.menuPlacement || 'auto', menuPortalTarget: props.menuPortalTarget, closeMenuOnScroll: props.closeMenuOnScroll, maxMenuHeight: props.maxMenuHeight, filterOption: !!props.customFilterOption ? props.customFilterOption : createFilter(props.filterOption), ref: ref, required: props.required, classNamePrefix: props.classNamePrefix, isValueInvalid: props.isValueInvalid, "aria-label": props.ariaLabel }));
});
//# sourceMappingURL=Select.js.map